import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useConnectGmail } from "@components/settings/SendingDomains/useConnectGmail";
import { useWorkspaceSendingEmails } from "@hooks/useWorkspaceSendingEmails";

interface GmailConnectContextValue {
    // Opens the shared Gmail OAuth popup; on success it creates the sending
    // domain and auto-selects the mailbox it created.
    connect: () => void;
    // True while the OAuth popup is open or the create-domain mutation runs.
    isConnecting: boolean;
}

const GmailConnectContext = createContext<GmailConnectContextValue | null>(
    null,
);

// The Gmail OAuth flow is hoisted to the workspace — which stays mounted across
// the Details and Send steps — so an in-flight authorization survives a step
// change. If it lived in the step's From picker, switching steps would unmount
// that instance and its window-message listener, and the popup's returned code
// would land on nothing: no domain created, no feedback, no auto-selection.
// The provider also owns the auto-select of the freshly connected mailbox for
// the same reason.
export const NativeInvoiceGmailConnectProvider: React.FC<{
    onSenderConnected: (sendingEmailId: string) => void;
    children: React.ReactNode;
}> = ({ onSenderConnected, children }) => {
    const { data: sendingEmails } = useWorkspaceSendingEmails();

    // Once the newly-connected Gmail domain shows up in `sendingEmails` (its
    // creation invalidates the query, same as the Email Sender Settings
    // modal's flow), select the mailbox it created automatically so the user
    // doesn't have to reopen the dropdown to pick the address they just
    // connected.
    const [pendingGmailDomainId, setPendingGmailDomainId] = useState<
        string | null
    >(null);
    useEffect(() => {
        if (!pendingGmailDomainId || !sendingEmails) {
            return;
        }
        const connectedEmail = sendingEmails.find(
            (email) => email.sendingDomainId === pendingGmailDomainId,
        );
        if (connectedEmail) {
            onSenderConnected(connectedEmail.id);
            setPendingGmailDomainId(null);
        }
    }, [pendingGmailDomainId, sendingEmails, onSenderConnected]);

    const { connect, isConnecting } = useConnectGmail({
        onConnected: (domain) => setPendingGmailDomainId(domain.id),
    });

    const value = useMemo<GmailConnectContextValue | null>(
        () => ({ connect, isConnecting }),
        [connect, isConnecting],
    );

    return (
        <GmailConnectContext.Provider value={value}>
            {children}
        </GmailConnectContext.Provider>
    );
};

export function useNativeInvoiceGmailConnect(): GmailConnectContextValue | null {
    return useContext(GmailConnectContext);
}
